import React, { useState } from 'react';
import { Table, Spinner, Form, Row, Col, Button, Alert, CardFooter } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';

import { Card, CardBody, CardHeader } from '@components/card/card';
import { ntxAPI } from '@API/axiosNeatex';
import { useTranslation } from 'react-i18next';
import type { APIResponse } from '@store/Shared/shared';

import useGetInventoryWithPermission from '@hooks/NTX/Organization/useGetInventoryWithPermission';
import { TreeSelect } from 'antd';
import useGetSubinventoryList from '@hooks/INV/Inventories/useGetSubinventoryList';
import Loading from '@components/Shared/Loader/Loading';

// ✅ Picker
import { useItemUomPicker, ItemUomPickerModal } from '@pages/INV/lookups/useItemUomPicker';
import type { ItemWithUomRow } from '@pages/INV/lookups/itemsWithUomLookup.api';

interface StockCard {
  itemId: string;
  itemCode: string | null;
  itemName: string | null;
  itemName2: string | null;
  transactionCode: string | null;
  transactionDate: string;
  transactionTypeId: string | null;
  transactionTypeName: string;
  transactionTypeName2: string | null;
  transactionQuantityIn: number;
  transactionQuantityOut: number;
  unitPrice: number | null;
  transactionQuantityTotal: number;
}

const StockCardReport: React.FC = () => {
  const [data, setData] = useState<StockCard[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { InventoryOptions, loading: loading2, error: error2 } = useGetInventoryWithPermission();
  const { TreeSubInventoryData, loading: loading3, error: error3 } = useGetSubinventoryList();
  const { t, i18n } = useTranslation();

  const [itemId, setItemId] = useState<string | null>(null);
  const [inventoryId, setInventoryId] = useState<string | null>(null);
  const [subInventoryId, setSubInventoryId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;

  // ✅ Item & UOM Picker
  const { pickItemUom, modalProps } = useItemUomPicker();
  const [pickedItem, setPickedItem] = useState<ItemWithUomRow | null>(null);

  const getPickedItemLabel = (r: ItemWithUomRow | null) => {
    if (!r) return '';
    const name = i18n.language === 'ar' ? r.name2 || r.name : r.name || r.name2;
    const code = r.itemCode ?? '';
    return `${code} - ${name ?? ''}`.trim();
  };

  const clearPickedItem = () => {
    setPickedItem(null);
    setItemId(null);
  };

  const openItemPicker = async () => {
    const res = await pickItemUom({
      title: i18n.language === 'en' ? 'Select Item & UOM' : 'اختيار صنف ووحدة',

      params: {
        // ✅ اختياري: يمرر المخزن للـ lookup حتى يعرض الكميات إن كانت مدعومة
        InventoryId: inventoryId || undefined,

        // ⚠️ إذا عندك Scope إلزامي بالباك-إند عدل القيمة حسب نظامك
        // Scope: 'INV',

        WithParent: true,
        WithImage: false,
        WithLots: false,
        OnlyEnabled: true
      } as any,
      pageSize: 15,
      // searchMode: 'auto',
      searchMode: 'manual',
      initialSearchField: 'itemCode',
      fixedFields: ['itemCode', 'name', 'barcode', 'unitOfMeasureName'],
      dynamicFields: inventoryId ? ['quentity', 'reservedQty'] : []
    });

    if (res?.row) {
      setPickedItem(res.row);
      setItemId(res.row.itemId); // ✅ هذا اللي يحتاجه تقرير Stock Card
    }
  };

  const fetchData = async () => {
    if (!itemId) {
      setError(t('ItemRequired'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await ntxAPI.get<APIResponse<StockCard[]>>(`InventoryReports/GetStockCard`, {
        params: {
          ItemId: itemId,
          InventoryId: inventoryId,
          SubinventoryId: subInventoryId,
          StartDate: startDate,
          EndDate: endDate
        }
      });

      if (data.succeeded === true) {
        setData(data.data);
      } else {
        setError(data instanceof Error ? data.message : t('UnknownError'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('UnknownError'));
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    clearPickedItem();
    setInventoryId(null);
    setSubInventoryId(null);
    setDateRange([null, null]);
  };

  const formatDate = (dateString: string): string => {
    if (dateString === '0001-01-01T00:00:00') return t('OpeningBalance');
    return new Date(dateString).toLocaleDateString();
  };

  const formatQuantity = (quantity: number): string => {
    return new Intl.NumberFormat('en-US').format(quantity);
  };

  return (
    <div className='container mt-4'>
      <Card>
        <CardHeader className='d-flex justify-content-between align-items-center'>
          <h5 className='mb-0'>{i18n.language === 'en' ? 'Stock Card Report' : 'تقرير بطاقة المخزون'}</h5>
          {data.length > 0 && <span className='me-2'>Showing: {data.length} transactions</span>}
        </CardHeader>

        <CardBody>
          {error && (
            <Alert variant='danger' className='mb-4'>
              {t('ErrorLoadingData')}: {error}
            </Alert>
          )}

          <Card className='mb-4'>
            <CardHeader>{t('Filters')}</CardHeader>

            <Loading loading={loading2 || loading3} error={error2 || error3}>
              <CardBody>
                <Form>
                  <Row className='g-3'>
                    {/* ✅ Item Picker بدل react-select */}
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('itemName')}</Form.Label>

                        <div className='d-flex gap-2'>
                          <Form.Control
                     
                            value={getPickedItemLabel(pickedItem)}
                            placeholder={t('Choose')}
                            readOnly
                            onClick={openItemPicker}
                            style={{ cursor: 'pointer' }}
                          />

                          <Button variant='outline-theme' size='sm' onClick={openItemPicker}>
                            <i className='bi bi-search  ' />
                           </Button>

                          {pickedItem && (
                            <Button
                              variant='outline-secondary'
                              size='sm'
                              onClick={clearPickedItem}
                              title={t('ResetFilters')}
                              aria-label='clear-item'
                            >
                              <i className='bi bi-x-lg' />
                            </Button>
                          )}
                        </div>

                        {pickedItem && (
                          <div className='text-muted small mt-1'>
                            {i18n.language === 'en' ? 'UOM:' : 'الوحدة:'}{' '}
                            {i18n.language === 'ar'
                              ? pickedItem.unitOfMeasureName2 || pickedItem.unitOfMeasureName
                              : pickedItem.unitOfMeasureName || pickedItem.unitOfMeasureName2}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      {/* <Form.Group>
                        <Form.Label>{t('Inventory')}</Form.Label>
                        <select
                          className='form-select'
                          value={inventoryId ?? ''}
                          onChange={e => {
                            const v = e.target.value || null;
                            setInventoryId(v);
                            setSubInventoryId(null);
                          }}
                        >
                          <option value=''>{t('Choose')}</option>
                          {InventoryOptions?.map((opt: any) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </Form.Group> */}
                      <div className='form-group mb-3'>
                        <label className='form-label'>{t('المخزن')}</label>
                        <Select
                          className={' '}
                           classNamePrefix='react-select'
                          isLoading={loading3}
                          isSearchable
                          isClearable
                          options={InventoryOptions}
                          // value={
                          //   values.inventoryId && InventoryOptions?.find((f: any) => f.value === values.inventoryId)
                          // }
                          onChange={(e: any) => {
                            const v = e.value || null;
                            setInventoryId(v);
                            setSubInventoryId(null);
                          }}
                          placeholder={t('Choose')}
                        />
                      </div>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('المخزن الفرعي')}</Form.Label>
                        <TreeSelect
                          loading={loading3}
                          showSearch
                          className='w-100'
                          treeLine
                          placeholder={t('Choose')}
                          allowClear
                          treeDefaultExpandAll={false}
                          value={subInventoryId}
                          onChange={(option: any) => setSubInventoryId(option === null ? null : option)}
                          treeData={TreeSubInventoryData?.filter((f: any) => f.parentId === inventoryId)}
                          treeNodeFilterProp='title'
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>{t('DateRange')}</Form.Label>
                        <DatePicker
                          selectsRange
                          startDate={startDate}
                          endDate={endDate}
                          onChange={update => setDateRange(update)}
                          isClearable
                          className='form-control text-center'
                          placeholderText={t('SelectDateRange')}
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12} className='text-end'>
                      <Button variant='secondary' onClick={resetFilters} className='me-2'>
                        {t('ResetFilters')}
                      </Button>
                      <Button variant='primary' onClick={fetchData} disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner
                              as='span'
                              animation='border'
                              size='sm'
                              role='status'
                              aria-hidden='true'
                              className='me-2'
                            />
                            {t('Loading')}
                          </>
                        ) : (
                          t('LoadReport')
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Loading>
          </Card>

          {loading ? (
            <div className='text-center my-5'>
              <Spinner animation='border' role='status'>
                <span className='visually-hidden'>{t('Loading')}</span>
              </Spinner>
              <p className='mt-2'>{t('LoadingStockCardData')}</p>
            </div>
          ) : data.length > 0 ? (
            <div className='table-responsive'>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{t('Date')}</th>
                    <th>{t('TransactionCode')}</th>
                    <th>{t('TransactionType')}</th>
                    <th>{t('itemName')}</th>
                    <th>{t('ItemCode')}</th>
                    <th>{t('QuantityIn')}</th>
                    <th>{t('QuantityOut')}</th>
                    <th>{t('TotalQuantity')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.map((item, index) => (
                    <tr key={`${item.itemId}-${item.transactionCode}-${index}`}>
                      <td>{formatDate(item.transactionDate)}</td>
                      <td>{item.transactionCode || '-'}</td>
                      <td>
                        <div>{item.transactionTypeName}</div>
                        {item.transactionTypeName2 && (
                          <div className='text-muted small'>{item.transactionTypeName2}</div>
                        )}
                      </td>
                      <td>
                        {item.itemName ? (
                          <>
                            <div>{item.itemName}</div>
                            {item.itemName2 && <div className='text-muted small'>{item.itemName2}</div>}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>{item.itemCode || '-'}</td>
                      <td className='text-success'>
                        {item.transactionQuantityIn > 0 ? formatQuantity(item.transactionQuantityIn) : '-'}
                      </td>
                      <td className='text-danger'>
                        {item.transactionQuantityOut > 0 ? formatQuantity(item.transactionQuantityOut) : '-'}
                      </td>
                      <td className='fw-bold'>{formatQuantity(item.transactionQuantityTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className='text-center py-5'>
              <p>{t('NoDataAvailable')}</p>
            </div>
          )}
        </CardBody>

        {data.length > 0 && (
          <CardFooter className='text-muted'>
            {t('ReportGeneratedOn')} {new Date().toLocaleString()}
          </CardFooter>
        )}
      </Card>

      {/* ✅ مودال اختيار الصنف/الوحدة */}
      <ItemUomPickerModal {...modalProps} />
    </div>
  );
};

export default StockCardReport;
